import React, { useState, useEffect, useRef, useCallback } from 'react';

const ImageCropper = ({ imageSrc, onCrop, onCancel }) => {
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const containerRef = useRef(null);
    const [crop, setCrop] = useState({ x: 0, y: 0, width: 190, height: 190 });
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageObj, setImageObj] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [isResizing, setIsResizing] = useState(false);
    const [resizeHandle, setResizeHandle] = useState('');
    const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            setImageObj(img);
            setImageLoaded(true);

            // Calculate display size to fit in container (max 500px)
            const maxSize = 500;
            const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
            const displayWidth = img.width * scale;
            const displayHeight = img.height * scale;

            setDisplaySize({ width: displayWidth, height: displayHeight });

            // Set initial crop to center of image
            const cropSize = Math.min(displayWidth, displayHeight) * 0.6; // 60% of smaller dimension
            setCrop({
                x: (displayWidth - cropSize) / 2,
                y: (displayHeight - cropSize) / 2,
                width: cropSize,
                height: cropSize
            });
        };
        img.src = imageSrc;
    }, [imageSrc]);

    const handleMouseDown = (e, action, handle = '') => {
        e.preventDefault();
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (action === 'drag') {
            setIsDragging(true);
            setDragStart({ x: x - crop.x, y: y - crop.y });
        } else if (action === 'resize') {
            setIsResizing(true);
            setResizeHandle(handle);
            setDragStart({ x, y });
        }
    };

    const handleMouseMove = useCallback((e) => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (isDragging) {
            const newX = Math.max(0, Math.min(x - dragStart.x, displaySize.width - crop.width));
            const newY = Math.max(0, Math.min(y - dragStart.y, displaySize.height - crop.height));

            setCrop(prev => ({
                ...prev,
                x: newX,
                y: newY
            }));
        } else if (isResizing) {
            const deltaX = x - dragStart.x;
            const deltaY = y - dragStart.y;

            setCrop(prev => {
                let newCrop = { ...prev };

                switch (resizeHandle) {
                    case 'se': // bottom-right
                        newCrop.width = Math.max(50, Math.min(prev.width + deltaX, displaySize.width - prev.x));
                        newCrop.height = Math.max(50, Math.min(prev.height + deltaY, displaySize.height - prev.y));
                        break;
                    case 'sw': // bottom-left
                        const newWidth = Math.max(50, prev.width - deltaX);
                        const newX = Math.max(0, prev.x + prev.width - newWidth);
                        newCrop.x = newX;
                        newCrop.width = newWidth;
                        newCrop.height = Math.max(50, Math.min(prev.height + deltaY, displaySize.height - prev.y));
                        break;
                    case 'ne': // top-right
                        const newHeight = Math.max(50, prev.height - deltaY);
                        const newY = Math.max(0, prev.y + prev.height - newHeight);
                        newCrop.y = newY;
                        newCrop.height = newHeight;
                        newCrop.width = Math.max(50, Math.min(prev.width + deltaX, displaySize.width - prev.x));
                        break;
                    case 'nw': // top-left
                        const newWidthNW = Math.max(50, prev.width - deltaX);
                        const newHeightNW = Math.max(50, prev.height - deltaY);
                        const newXNW = Math.max(0, prev.x + prev.width - newWidthNW);
                        const newYNW = Math.max(0, prev.y + prev.height - newHeightNW);
                        newCrop.x = newXNW;
                        newCrop.y = newYNW;
                        newCrop.width = newWidthNW;
                        newCrop.height = newHeightNW;
                        break;
                    default:
                        break;
                }

                return newCrop;
            });

            setDragStart({ x, y });
        }
    }, [isDragging, isResizing, dragStart, crop, displaySize, resizeHandle]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        setIsResizing(false);
        setResizeHandle('');
    }, []);

    useEffect(() => {
        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

    const handleCrop = () => {
        if (!imageObj || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        canvas.width = 190;
        canvas.height = 190;

        // Calculate actual coordinates on the original image
        const scaleX = imageObj.width / displaySize.width;
        const scaleY = imageObj.height / displaySize.height;

        const actualX = crop.x * scaleX;
        const actualY = crop.y * scaleY;
        const actualWidth = crop.width * scaleX;
        const actualHeight = crop.height * scaleY;

        // Draw the cropped image
        ctx.drawImage(
            imageObj,
            actualX, actualY, actualWidth, actualHeight,
            0, 0, 190, 190
        );

        canvas.toBlob((blob) => {
            onCrop(blob);
        }, 'image/jpeg', 0.9);
    };

    const resetCrop = () => {
        if (!imageLoaded) return;

        const cropSize = Math.min(displaySize.width, displaySize.height) * 0.6;
        setCrop({
            x: (displaySize.width - cropSize) / 2,
            y: (displaySize.height - cropSize) / 2,
            width: cropSize,
            height: cropSize
        });
    };

    return (
        <div className="image-cropper-modal">
            <div className="cropper-header">
                <h3>Crop Your Image</h3>
                <button className="close-btn" onClick={onCancel}>×</button>
            </div>
            <div className="cropper-content">
                {imageLoaded && (
                    <>
                        <div className="crop-instructions">
                            <p>Drag the crop area to reposition, or drag the corners to resize.</p>
                        </div>
                        <div
                            ref={containerRef}
                            className="crop-container"
                            style={{
                                position: 'relative',
                                display: 'inline-block',
                                userSelect: 'none'
                            }}
                        >
                            <img
                                ref={imageRef}
                                src={imageSrc}
                                alt="Crop preview"
                                className="crop-image"
                                style={{
                                    width: `${displaySize.width}px`,
                                    height: `${displaySize.height}px`,
                                    display: 'block',
                                    border: '2px solid #ddd',
                                    borderRadius: '8px'
                                }}
                                draggable={false}
                            />

                            {/* Crop overlay */}
                            <div
                                className="crop-overlay"
                                style={{
                                    position: 'absolute',
                                    left: `${crop.x}px`,
                                    top: `${crop.y}px`,
                                    width: `${crop.width}px`,
                                    height: `${crop.height}px`,
                                    border: '2px solid #007bff',
                                    borderRadius: '4px',
                                    cursor: isDragging ? 'grabbing' : 'grab',
                                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
                                    boxSizing: 'border-box'
                                }}
                                onMouseDown={(e) => handleMouseDown(e, 'drag')}
                            >
                                {/* Resize handles */}
                                <div
                                    className="resize-handle resize-handle-nw"
                                    style={{
                                        position: 'absolute',
                                        top: '-6px',
                                        left: '-6px',
                                        width: '12px',
                                        height: '12px',
                                        backgroundColor: '#007bff',
                                        border: '2px solid white',
                                        borderRadius: '50%',
                                        cursor: 'nw-resize'
                                    }}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                        handleMouseDown(e, 'resize', 'nw');
                                    }}
                                />
                                <div
                                    className="resize-handle resize-handle-ne"
                                    style={{
                                        position: 'absolute',
                                        top: '-6px',
                                        right: '-6px',
                                        width: '12px',
                                        height: '12px',
                                        backgroundColor: '#007bff',
                                        border: '2px solid white',
                                        borderRadius: '50%',
                                        cursor: 'ne-resize'
                                    }}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                        handleMouseDown(e, 'resize', 'ne');
                                    }}
                                />
                                <div
                                    className="resize-handle resize-handle-sw"
                                    style={{
                                        position: 'absolute',
                                        bottom: '-6px',
                                        left: '-6px',
                                        width: '12px',
                                        height: '12px',
                                        backgroundColor: '#007bff',
                                        border: '2px solid white',
                                        borderRadius: '50%',
                                        cursor: 'sw-resize'
                                    }}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                        handleMouseDown(e, 'resize', 'sw');
                                    }}
                                />
                                <div
                                    className="resize-handle resize-handle-se"
                                    style={{
                                        position: 'absolute',
                                        bottom: '-6px',
                                        right: '-6px',
                                        width: '12px',
                                        height: '12px',
                                        backgroundColor: '#007bff',
                                        border: '2px solid white',
                                        borderRadius: '50%',
                                        cursor: 'se-resize'
                                    }}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                        handleMouseDown(e, 'resize', 'se');
                                    }}
                                />
                            </div>
                        </div>
                        <canvas
                            ref={canvasRef}
                            style={{ display: 'none' }}
                        />
                    </>
                )}
                <div className="cropper-controls">
                    <button className="reset-crop-btn" onClick={resetCrop}>
                        Reset
                    </button>
                    <button className="cancel-crop-btn" onClick={onCancel}>
                        Cancel
                    </button>
                    <button className="apply-crop-btn" onClick={handleCrop}>
                        Apply Crop
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageCropper;